import { ReactElement, useMemo, useCallback } from 'react'
import { getNodeY, getNodeHeight, PushTreeState } from 'ducks/pushTreeMap'
import { GROUP, LIST, responsivePositioningOptions } from '@adalo/constants'
import { useSelector } from 'react-redux'
import { LayoutChangeEvent } from 'react-native'
import {
  getResponsiveObject,
  getRenderObject,
  getPositioningLayout,
} from 'utils/responsive'

import { RunnerObject, Layout } from 'types'

import useWindowDimensions from 'hooks/useWindowDimensions'

const { FIXED_ON_SCROLL } = responsivePositioningOptions

export const useResponsiveObject = (
  object: RunnerObject,
  newY: number
): { renderObject: RunnerObject; positioningLayout: Layout } => {
  const { width } = useWindowDimensions()

  const deviceObject = useMemo(
    () => getResponsiveObject(object, width),
    [object, width]
  )

  const renderObject = useMemo(
    () => getRenderObject(deviceObject),
    [deviceObject]
  )

  const positioningLayout = getPositioningLayout(deviceObject, newY)

  return { renderObject, positioningLayout }
}

type State = { pushTreeMap: PushTreeState }

export const usePushNode = (
  componentId: string,
  pushId: string,
  isFixed: boolean
): { newY: number; nodeHeight: number } => {
  if (isFixed) {
    return { nodeHeight: 0, newY: 0 }
  }
  const newY = useSelector(state =>
    getNodeY(state as State, componentId, pushId)
  )

  const nodeHeight = useSelector(state =>
    getNodeHeight(state as State, componentId, pushId)
  )

  return { newY, nodeHeight }
}

export type HandleLayoutChangeFunction = (e: LayoutChangeEvent) => void

export interface HandleLayoutParameters {
  renderObject: RunnerObject
  setHeight: (pushId: string, obj: RunnerObject, height: number) => void
  nodeHeight: number
  pushId: string
}

export const useHandleLayoutChange = ({
  renderObject,
  setHeight,
  nodeHeight,
  pushId,
}: HandleLayoutParameters): HandleLayoutChangeFunction => {
  const handleLayoutChange = useCallback(
    (event: LayoutChangeEvent): void => {
      const { height: newHeight } = event.nativeEvent.layout

      if (
        renderObject.type !== GROUP &&
        renderObject.type !== LIST &&
        renderObject.responsivity?.verticalPositioning !== FIXED_ON_SCROLL &&
        newHeight !== nodeHeight
      ) {
        setHeight(pushId, renderObject, newHeight)
      }
    },
    [renderObject, setHeight, nodeHeight, pushId]
  )

  return handleLayoutChange
}

export const useRenderedChildren = (
  renderObject: RunnerObject,
  renderChildren: (children: RunnerObject[]) => ReactElement[]
): null | ReactElement[] => {
  const { children } = renderObject

  const isResponsiveList = renderObject.type === LIST

  const renderedChildren = useMemo(
    () => (children && !isResponsiveList ? renderChildren(children) : null),
    [children]
  )

  return renderedChildren
}
